import React, { useCallback, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useHistory } from "react-router";

import { cleanError as cleanErrorGeneral, cleanForbidden } from "../store/ducks/duckError";
import { cleanError } from "../store/ducks/duckAuth";
import { getLayouts } from "../store/ducks/duckLayout";
import { getMenuItems } from "../store/ducks/duckMenuItem";
import { API_ERROR_TOAST_EVENT } from "../utils/apiErrorToast";

import { useAppInsights } from "./AppInsightsProvider";
import DetailsError from "./DetailsError";
export const isIE = () => {
  //Navegador
  const ua = window.navigator.userAgent;

  // IE 10 or older
  const msie = ua.indexOf("MSIE ");
  // IE 11
  const trident = ua.indexOf("Trident/");
  // Edge (IE 12+)
  const edge = ua.indexOf("Edge/");

  return msie > 0 || trident > 0 || edge > 0;
};

const SESSION_ERROR = "Could not find sessions in MicroStrategy";

const Notifier = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const applicationIns = useAppInsights();
  const [firstRender, setFirstRender] = useState(true);
  const history = useHistory();
  const layouts = useSelector((state) => state.layout.dataLayout);
  const menuItems = useSelector((state) => state.menuItem.data);

  if (firstRender) {
    dispatch(getLayouts());
    dispatch(getMenuItems());
    setFirstRender(false);
  }

  const getLayoutName = () => {
    let locationArray = history.location.pathname.split("/");
    let arrayAdministration = [
      "Source",
      "MenuItem",
      "Filter",
      "Layout",
      "Groups",
      "Analytics",
      "Hr",
      "SearchUser",
    ];
    let actualLocation = locationArray.pop();

    if (arrayAdministration.includes(actualLocation)) {
      return `Administration - ${actualLocation}`;
    }
    switch (actualLocation) {
      case "PRE":
        return "PRE";
        break;
      case "Annual":
        return "Annual Budget";
        break;
      case "Documents":
        return "HR - Documents";
        break;
      case "":
        return "Home";
        break;
      default:
        return actualLocation;
        break;
    }
  };

  let id = getLayoutName();

  let layoutVisited = "";

  if (layouts.length > 0 && menuItems.length > 0) {
    layoutVisited = !isNaN(id) ? layouts?.find((x) => x.id == id) : id;

    if (typeof layoutVisited == "object") {
      let menuItemChildren = menuItems?.find((x) => x.id == layoutVisited.menuItemId);
      let menuItemParent = menuItems?.find((x) => x.id == menuItemChildren?.parentMenuItemId);
      let menuItemTop;

      menuItemParent?.parentMenuItemId
        ? ((menuItemTop = menuItems?.find((x) => x.id == menuItemParent?.parentMenuItemId)),
          (layoutVisited = `${menuItemTop?.name} - ${menuItemParent?.name} - ${layoutVisited.name}`))
        : (layoutVisited = `${menuItemParent?.name} - ${layoutVisited.name}`);
    }
  }

  if (
    typeof layoutVisited !== "object" &&
    layoutVisited !== "" &&
    localStorage.getItem("Email") != ""
  ) {
    applicationIns.setAuthenticatedUserContext(localStorage.getItem("Email"));
    applicationIns.trackPageView({
      name: layoutVisited,
      url: window.location.href,
      properties: {
        appName: "Reports",
      },
    });
  }

  const pathname = history.location.pathname;

  const { hasError, messageError: errorMessage } = useSelector((state) => state.auth);
  const {
    hasError: hasErrorGeneral,
    message: messageG,
    details: detailsG,
    isUnauthorized,
    idRedirect,
  } = useSelector((state) => state.error);

  if (pathname !== "/Unauthorized") {
    localStorage.setItem("Path", pathname);
  }

  const lastLoadSession = useSelector((state) => state.session.lastLoadSession);
  const anyValidSession = useSelector((state) => {
    let sessions = state.session.data;

    if (
      sessions.length === 0 ||
      sessions.every((x) => x.sessions && x.sessions.every((x) => x.session === null))
    ) {
      return false;
    }

    return true;
  });

  useEffect(() => {
    if (lastLoadSession && !anyValidSession) {
      enqueueSnackbar(SESSION_ERROR, {
        persist: true,
        content: <DetailsError key={`Error`} errorMessage={errorMessage} message={SESSION_ERROR} />,
      });
    }
  }, [enqueueSnackbar, errorMessage, anyValidSession, lastLoadSession]);

  const handleClose = useCallback(
    (key) => (e) => {
      e.preventDefault();
      closeSnackbar(key);
    },
    [closeSnackbar],
  );

  //isIE
  useEffect(() => {
    if (isIE()) {
      enqueueSnackbar("Compatibility Problems. Please open in Chrome or Microsoft Edge(Chromium)", {
        variant: "warning",
        action: (key) => (
          <>
            <Tooltip title="Open in Microsoft Edge">
              <Button color="inherit" href={`microsoft-edge:${window.location}`} size="small">
                <img
                  alt="open-in-microsoft-edge"
                  src={`${process.env.REACT_APP_BASE_API}/edge.png`}
                  style={{ marginLeft: 5 }}
                />
              </Button>
            </Tooltip>
            <Tooltip title="Dismiss" onClick={handleClose(key)}>
              <IconButton color="inherit" size="small">
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </>
        ),
        persist: true,
      });
    }
  }, [enqueueSnackbar, handleClose]);

  useEffect(() => {
    if (hasError) {
      enqueueSnackbar("", {
        persist: false,
        content: <DetailsError key="Error" errorMessage={errorMessage} />,
      });
      dispatch(cleanError());
    }
  }, [enqueueSnackbar, hasError, errorMessage, applicationIns, dispatch]);

  useEffect(() => {
    if (hasErrorGeneral) {
      enqueueSnackbar("", {
        persist: true,
        content: (
          <DetailsError
            key="Error-General"
            label={messageG || "An unexpected error has occurred"}
            errorMessage={detailsG || messageG}
          />
        ),
      });
      dispatch(cleanErrorGeneral());
    }
  }, [
    enqueueSnackbar,
    hasErrorGeneral,
    messageG,
    detailsG,
    applicationIns,
    errorMessage,
    dispatch,
  ]);

  useEffect(() => {
    const onApiErrorToast = (event) => {
      const message = event?.detail?.message;
      const details = event?.detail?.details;

      if (!message) {
        return;
      }

      enqueueSnackbar("", {
        persist: false,
        content: (
          <DetailsError
            key={`Api-Error-${Date.now()}`}
            label={message}
            errorMessage={details || message}
          />
        ),
      });
    };

    window.addEventListener(API_ERROR_TOAST_EVENT, onApiErrorToast);

    return () => {
      window.removeEventListener(API_ERROR_TOAST_EVENT, onApiErrorToast);
    };
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (isUnauthorized) {
      dispatch(cleanForbidden());
      history.push(`/Unauthorized`, idRedirect);
    }
  }, [isUnauthorized, enqueueSnackbar, dispatch, history]);

  return null;
};

export default Notifier;
